import { createRouter, createWebHistory, type NavigationGuardWithThis, type RouterOptions } from 'vue-router';

import { ui } from '@/configuration';
import { authReady, getUser, login } from '@/services/auth';
import { useAuthStore } from '@/stores/auth';
import About from '@/views/AboutView.vue';
import Collection from '@/views/CollectionView.vue';
import EntityView from '@/views/EntityView.vue';
import File from '@/views/FileView.vue';
import Home from '@/views/HomeView.vue';
import List from '@/views/ListView.vue';
import NotFound from '@/views/NotFoundView.vue';
import CallbackOauth from '@/views/OauthCallbackView.vue';
import ObjectView from '@/views/ObjectView.vue';
import PersonView from '@/views/PersonView.vue';
import Privacy from '@/views/PrivacyView.vue';
import SearchMap from '@/views/SearchMapView.vue';
import Search from '@/views/SearchView.vue';
import Shell from '@/views/ShellView.vue';
import Terms from '@/views/TermsView.vue';
import User from '@/views/UserView.vue';

const routes: RouterOptions['routes'] = [
  {
    path: '/',
    name: 'root',
    component: Shell,
    children: [
      {
        path: '',
        name: 'home',
        component: Home,
      },
      {
        path: '/search',
        name: 'search',
        component: Search,
      },
      // Maps depend on OpenStreetMap tiles, so they are unavailable in offline
      // deployments (e.g. local-network Raspberry Pis). When disabled the route
      // is not registered and /map falls through to the 404 view.
      ...(ui.features?.disableMaps
        ? []
        : [
            {
              path: '/map',
              name: 'map',
              component: SearchMap,
            },
          ]),
      {
        path: '/list',
        name: 'list',
        component: List,
      },
      {
        path: '/collection',
        name: 'collection',
        component: Collection,
      },
      {
        path: '/object',
        name: 'object',
        component: ObjectView,
        children: [],
      },
      {
        path: '/entity',
        name: 'entity',
        component: EntityView,
      },
      {
        path: '/person',
        name: 'person',
        component: PersonView,
      },
      {
        path: '/file',
        name: 'file',
        component: File,
      },
      {
        path: '/about',
        name: 'about',
        component: About,
      },
      {
        path: '/terms',
        name: 'terms',
        component: Terms,
      },
      {
        path: '/privacy',
        name: 'privacy',
        component: Privacy,
      },
      {
        path: '/user',
        name: 'user',
        component: User,
        meta: {
          requiresAuth: true,
        },
      },
      { path: '/:pathMatch(.*)*', name: 'NotFound', component: NotFound },
    ],
  },
  {
    path: '/auth/callback',
    component: CallbackOauth,
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, _from, savedPosition) {
    return new Promise((resolve) => {
      if (savedPosition) {
        // Wait for the DOM to update before scrolling
        setTimeout(() => {
          resolve(savedPosition);
        }, 500);
      } else if (to.hash) {
        resolve({ el: to.hash });
      } else {
        resolve({ top: 0 });
      }
    });
  },
});

const onAuthRequired: NavigationGuardWithThis<undefined> = async (to) => {
  // Wait for initAuth() on every navigation so views don't fire API calls
  // before startup auth hydration (e.g. expired-token refresh) has settled.
  // Anonymous startups resolve authReady immediately, so the cost is negligible.
  await authReady;

  const authStore = useAuthStore();
  const user = await getUser();

  if (user) {
    return true;
  }

  // OIDC says no valid user — reset Pinia store if it's out of sync
  if (authStore.isLoggedIn) {
    authStore.reset();
  }

  if (to.meta?.requiresAuth) {
    login();

    return false;
  }

  return true;
};

router.beforeEach(onAuthRequired);

export default router;
